'use client';

import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { categoryLabels } from '@/lib/dcma-config';
import type { CriterionResult } from '@/lib/dcma-scoring-engine';

interface CriterionResultsTableProps {
  results: CriterionResult[];
}

function getTierBadge(tier: string, tierLabel: string) {
  switch (tier) {
    case 'good':
      return (
        <Badge
          variant="outline"
          className="border-success/30 bg-success/10 text-success text-xs font-medium"
        >
          {tierLabel}
        </Badge>
      );
    case 'acceptable':
      return (
        <Badge
          variant="outline"
          className="border-warning/30 bg-warning/10 text-warning text-xs font-medium"
        >
          {tierLabel}
        </Badge>
      );
    case 'bad':
    case 'badLow':
    case 'badHigh':
      return (
        <Badge
          variant="outline"
          className="border-destructive/30 bg-destructive/10 text-destructive text-xs font-medium"
        >
          {tierLabel}
        </Badge>
      );
    default:
      return (
        <Badge
          variant="outline"
          className="text-xs"
        >
          {tierLabel}
        </Badge>
      );
  }
}

function getImpactDisplay(impact: number, enabled: boolean) {
  if (!enabled) return <span className="text-xs text-muted-foreground">Disabled</span>;
  if (impact === 0) return <span className="text-xs text-success font-medium">No deduction</span>;
  return <span className="text-xs text-destructive font-semibold">{impact.toFixed(1)} pts</span>;
}

// Group results by category
function groupByCategory(results: CriterionResult[]): Map<string, CriterionResult[]> {
  const groups = new Map<string, CriterionResult[]>();
  for (const r of results) {
    const key = r.category;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  }
  return groups;
}

function CriterionRow({ result }: { result: CriterionResult }) {
  const [expanded, setExpanded] = useState(false);
  const hasFlaggedItems = result.flaggedItems.length > 0;

  return (
    <div
      className={`border-b border-border last:border-b-0 ${!result.enabled ? 'opacity-50' : ''}`}
    >
      <button
        type="button"
        onClick={() => hasFlaggedItems && setExpanded(!expanded)}
        className={`flex w-full items-center gap-4 px-4 py-3 text-left transition-colors ${
          hasFlaggedItems ? 'hover:bg-muted/50 cursor-pointer' : 'cursor-default'
        }`}
        disabled={!hasFlaggedItems}
        aria-expanded={hasFlaggedItems ? expanded : undefined}
      >
        {/* Expand icon */}
        <div className="w-4 shrink-0">
          {hasFlaggedItems ? (
            expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          ) : (
            <div className="h-4 w-4" />
          )}
        </div>

        {/* Criterion name + formula */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate">
              {result.criterionName}
            </span>
            {hasFlaggedItems && (
              <span className="text-xs text-muted-foreground">({result.flaggedItems.length})</span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground/70 font-mono truncate">
            {result.formula}
          </div>
        </div>

        {/* Value */}
        <div className="w-20 text-right shrink-0">
          <span className="text-sm font-mono font-semibold text-foreground">
            {result.displayValue}
          </span>
        </div>

        {/* Numerator / Denominator */}
        <div className="w-28 text-center shrink-0 hidden md:block">
          <span className="text-xs font-mono text-foreground">{result.numerator}</span>
          <span className="text-xs text-muted-foreground/50 mx-0.5">/</span>
          <span className="text-xs font-mono text-foreground">{result.denominator}</span>
          <div className="text-[10px] text-muted-foreground/60 leading-tight truncate">
            {result.denominatorLabel}
          </div>
        </div>

        {/* Tier badge */}
        <div className="w-24 shrink-0 flex justify-center">
          {getTierBadge(result.tier, result.tierLabel)}
        </div>

        {/* Impact */}
        <div className="w-24 text-right shrink-0">
          {getImpactDisplay(result.scoreImpact, result.enabled)}
        </div>
      </button>

      {/* Expanded flagged items */}
      {expanded && hasFlaggedItems && (
        <div className="border-t border-border bg-muted/30 px-4 py-3">
          <div className="ml-8">
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">
                Flagged Items ({result.flaggedItems.length})
              </span>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {result.flaggedItems.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 rounded-md bg-card px-3 py-2 text-xs"
                >
                  <code className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">
                    {item.activityId}
                  </code>
                  <span className="text-muted-foreground truncate">{item.activityName}</span>
                  <span className="ml-auto shrink-0 text-muted-foreground/70">{item.reason}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CriterionResultsTable({ results }: CriterionResultsTableProps) {
  const grouped = groupByCategory(results);
  const categories = ['relationships', 'schedule', 'float', 'path'];

  return (
    <Card>
      <CardHeader className="pb-0">
        <CardTitle className="text-base font-semibold">Score Breakdown</CardTitle>
      </CardHeader>
      <CardContent className="p-0 pt-4">
        {categories.map((catKey) => {
          const catResults = grouped.get(catKey);
          if (!catResults) return null;
          const label = categoryLabels[catKey] || catKey;

          return (
            <div key={catKey}>
              {/* Category header */}
              <div className="flex items-center gap-3 px-4 py-2 bg-muted/50">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {label}
                </span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Column headers */}
              <div className="flex items-center gap-4 px-4 py-1.5 text-xs text-muted-foreground">
                <div className="w-4 shrink-0" />
                <div className="flex-1">Criterion / Formula</div>
                <div className="w-20 text-right shrink-0">Value</div>
                <div className="w-28 text-center shrink-0 hidden md:block">Num / Denom</div>
                <div className="w-24 text-center shrink-0">Rating</div>
                <div className="w-24 text-right shrink-0">Impact</div>
              </div>

              {/* Rows */}
              {catResults.map((r) => (
                <CriterionRow
                  key={r.criterionId}
                  result={r}
                />
              ))}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
